import { t } from '../core/i18n.js';
import { escapeHtml } from '../core/utils.js';
import { device } from '../core/device.js';
import { catalog } from '../core/catalog.js';
import { notify } from '../core/notify.js';
import { customOrder } from '../core/custom-order.js';
import { groupByCategory } from './render.js';
import { applyGroupOrder, moveTo, orderBy, mergeSubsetOrder } from '../core/order-core.js';
import { stripCountry } from '../core/country.js';

/* Ordenar a mi gusto las hileras (categorías) y, en TV en directo, también los canales de cada una.
   - Con el dedo o el ratón: se arrastra por el asa (⋮⋮), o con los botones ▲ ▼.
   - Con el mando de la tele: OK sobre una fila la «coge» (se marca), ▲ ▼ la mueven y OK la suelta.
   Solo hileras: un canal suelto se mueve con una pulsación larga sobre su tarjeta (menú del canal, ver main.js).
   Cada cambio se guarda al momento. Al cerrar, la lista pasa a «Mi orden» (ver live-filters / library). */

/** Grupos de la sección en el orden actual (el guardado, o el del proveedor si no hay). */
export function currentGroups(section) {
    const kind = section;
    const groups = groupByCategory(catalog.visible(kind), catalog.categories[kind], null);
    return applyGroupOrder(groups, customOrder.get(section));
}

/** Las hileras que se ven ahora en pantalla (con los filtros puestos), en el orden actual. `items` = función con los elementos
    filtrados; sin ella, todas las hileras de la sección. Las hileras que un filtro oculta no se enseñan ni se tocan. */
export function shownGroups(section, items) {
    const all = currentGroups(section);
    if (typeof items !== 'function') return all;
    const shown = groupByCategory(items(), catalog.categories[section], null);
    return orderBy(shown, (g) => String(g.id), all.map((g) => String(g.id)));
}

export function openOrderEditor({ section, items = null, onClose = () => {} }) {
    const app = window.app;
    const id = 'order-' + Date.now().toString(36);
    const live = section === 'live';
    let groups = shownGroups(section, items);
    let grabbed = '';
    let focusId = '';

    const nameOf = (g) => stripCountry(g.name);
    const rowsOf = () => groups.map((g) => ({ id: g.id, name: nameOf(g), sub: String(g.items.length) }));

    const html = `<div class="popup order-popup" id="${id}">
        <div class="page">
            <div class="navbar"><div class="navbar-bg"></div><div class="navbar-inner">
                <div class="title" data-order-title>${escapeHtml(t('order.title'))}</div>
                <div class="right"><a href="#" class="link popup-close btn-icon" aria-label="${escapeHtml(t('common.close'))}"><span class="mi">close</span></a></div>
            </div></div>
            <div class="page-content">
                <p class="pw-note" data-order-hint></p>
                <div class="order-list" data-order-list role="list"></div>
                <div class="order-foot">
                    <button type="button" class="btn" data-order-reset data-focusable><span class="mi">restart_alt</span>${escapeHtml(t('order.reset'))}</button>
                    <button type="button" class="btn btn-accent" data-order-done data-focusable><span class="mi">check</span>${escapeHtml(t('order.done'))}</button>
                </div>
            </div>
        </div>
    </div>`;

    const popup = app.popup.create({
        content: html,
        on: {
            open(p) { setup(p.el || document.getElementById(id)); },
            closed(p) { if (window.IPTV && window.IPTV.pageBack && p && p.el && window.IPTV.pageBack === p.el.__pageBack) window.IPTV.pageBack = null; onClose({ changed: customOrder.has(section) }); }
        }
    });

    function setup(el) {
        const list = el.querySelector('[data-order-list]');
        const hint = el.querySelector('[data-order-hint]');

        const save = (ids) => {
            groups = ids.map((i) => groups.find((g) => g.id === i)).filter(Boolean);
            // Solo se mueven las hileras que se ven; las que un filtro oculta conservan su sitio.
            customOrder.set(section, { cats: mergeSubsetOrder(currentGroups(section).map((g) => String(g.id)), ids) });
        };

        const paint = () => {
            const rows = rowsOf();
            hint.textContent = t(device.isTV ? 'order.hintTv' : 'order.hint') + (live ? ' ' + t('order.hintLive') : '');
            list.innerHTML = rows.map((r, i) => `<div class="order-row${grabbed === r.id ? ' is-grabbed' : ''}" role="listitem" tabindex="0" data-focusable data-id="${escapeHtml(r.id)}" aria-grabbed="${grabbed === r.id}">
                <span class="mi order-handle" data-handle aria-hidden="true">drag_indicator</span>
                <span class="order-name">${escapeHtml(r.name)}</span>
                <span class="order-sub">${escapeHtml(r.sub)}</span>
                <button type="button" class="btn btn-icon" data-up tabindex="-1" aria-label="${escapeHtml(t('order.up'))}" ${i === 0 ? 'disabled' : ''}><span class="mi">arrow_upward</span></button>
                <button type="button" class="btn btn-icon" data-down tabindex="-1" aria-label="${escapeHtml(t('order.down'))}" ${i === rows.length - 1 ? 'disabled' : ''}><span class="mi">arrow_downward</span></button>
            </div>`).join('') || `<p class="pw-note">${escapeHtml(t('common.noResults'))}</p>`;
            if (focusId) {
                const r = list.querySelector('[data-id="' + CSS.escape(focusId) + '"]');
                if (r) { r.focus(); try { if (window.IPTV && window.IPTV.tvnav) window.IPTV.tvnav.focus(r); } catch (e) { /* sin navegación por foco */ } r.scrollIntoView({ block: 'nearest' }); }
            }
        };

        const idsNow = () => rowsOf().map((r) => r.id);
        const move = (rid, to) => {
            const ids = idsNow();
            const from = ids.indexOf(rid);
            if (from < 0) return;
            const target = Math.max(0, Math.min(ids.length - 1, to));
            if (target === from) return;
            save(moveTo(ids, from, target));
            focusId = rid;
            paint();
        };

        // Botones, abrir los canales de una hilera y coger/soltar con OK
        list.addEventListener('click', (e) => {
            const row = e.target.closest('.order-row');
            if (!row) return;
            const rid = row.getAttribute('data-id');
            if (e.target.closest('[data-up]')) { e.preventDefault(); return move(rid, idsNow().indexOf(rid) - 1); }
            if (e.target.closest('[data-down]')) { e.preventDefault(); return move(rid, idsNow().indexOf(rid) + 1); }
            if (e.target.closest('[data-handle]')) return; // el asa solo arrastra
            // Toque en la fila: coge / suelta
            toggleGrab(rid);
        });

        // Mando: con una fila cogida, ▲ ▼ la mueven (y no mueven el foco); OK o Atrás la sueltan.
        const toggleGrab = (rid) => { grabbed = grabbed === rid ? '' : rid; focusId = rid; paint(); };
        el.addEventListener('keydown', (e) => {
            // (El OK del mando o Enter sobre la fila ya llega como un clic: lo gestiona la navegación por foco y coge o suelta la fila.)
            if (!grabbed) return;
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault(); e.stopPropagation();
                move(grabbed, idsNow().indexOf(grabbed) + (e.key === 'ArrowUp' ? -1 : 1));
            } else if (e.key === 'Backspace') {
                e.preventDefault(); e.stopPropagation();
                focusId = grabbed; grabbed = ''; paint();
            }
        }, true);
        // «Atrás» (botón del móvil, tecla de la tele): primero suelta la fila cogida y luego vuelve de los canales a las hileras; si no, cierra.
        const pageBack = () => {
            if (grabbed) { focusId = grabbed; grabbed = ''; paint(); return true; }
            return false;
        };
        window.IPTV.pageBack = pageBack;
        el.__pageBack = pageBack;

        // Arrastrar por el asa (dedo o ratón)
        let drag = null;
        list.addEventListener('pointerdown', (e) => {
            const handle = e.target.closest('[data-handle]');
            const row = handle && handle.closest('.order-row');
            if (!row) return;
            e.preventDefault();
            drag = { id: row.getAttribute('data-id'), row, pointer: e.pointerId };
            row.classList.add('is-dragging');
            try { handle.setPointerCapture(e.pointerId); } catch (err) { /* sin captura */ }
        });
        list.addEventListener('pointermove', (e) => {
            if (!drag || e.pointerId !== drag.pointer) return;
            const rows = Array.from(list.querySelectorAll('.order-row'));
            const idx = rows.findIndex((r) => { const b = r.getBoundingClientRect(); return e.clientY >= b.top && e.clientY <= b.bottom; });
            if (idx < 0) return;
            const cur = rows.indexOf(drag.row);
            if (idx !== cur) list.insertBefore(drag.row, idx > cur ? rows[idx].nextSibling : rows[idx]);
        });
        const drop = (e) => {
            if (!drag || e.pointerId !== drag.pointer) return;
            const ids = Array.from(list.querySelectorAll('.order-row')).map((r) => r.getAttribute('data-id'));
            const did = drag.id;
            drag = null;
            save(ids);
            focusId = did;
            paint();
        };
        list.addEventListener('pointerup', drop);
        list.addEventListener('pointercancel', drop);

        el.querySelector('[data-order-done]').addEventListener('click', () => popup.close());
        el.querySelector('[data-order-reset]').addEventListener('click', () => {
            notify.confirm(t('order.resetConfirm'), t('order.reset'), () => {
                customOrder.reset(section);
                groups = shownGroups(section, items);
                grabbed = ''; focusId = '';
                paint();
            });
        });
        paint();
        if (device.isTV && window.IPTV && window.IPTV.tvnav) setTimeout(() => window.IPTV.tvnav.focusLayer(el), 120);
    }

    popup.open();
    return popup;
}
